import { proxy, wrap } from 'comlink';
import { createWorker } from '../../utils/workerUtils';
import { DEFAULT_MAX_BUFFERED_FRAMES, Track, } from './Track';
import { destroyDummyStream, extractAVCParams, extractHEVCParams, lengthPrefixedToAnnexB, prependNalus, WebRTCVideoDecoder, } from './webrtcvideodecoder';
/**
 * Main-thread replacement for VideoDecoderWorker on Safari/iOS.
 *
 * Uses WebRTCVideoDecoder instances (WebRTC loopback) instead of
 * WebCodecs VideoDecoder to bypass Safari's 2-decoder hardware limit.
 *
 * Implements the same interface as VideoDecoderWorker so it can be
 * assigned to AbstractVideoEngine.videoDecoderWorkerProxy as a drop-in.
 */
export class SafariVideoDecoder {
    LOG_PREFIX = '[SafariVideoDecoder]';
    playerPortProxy;
    tracks = new Map();
    creatingTracks = new Set();
    waitingForKeyframe = new Set();
    nextDecoderId = 0;
    frameReaderWorker = null;
    frameReaderPort = null;
    injectorWorker = null;
    decoderById = new Map();
    async init() {
        console.log(`${this.LOG_PREFIX} init called`);
        this._initFrameReaderWorker();
        this._initInjectorWorker();
    }
    _initFrameReaderWorker() {
        try {
            const worker = this._createTrackProcessorWorker();
            const channel = new MessageChannel();
            worker.postMessage({ type: 'init', port: channel.port2 }, [
                channel.port2,
            ]);
            channel.port1.onmessage = (e) => {
                if (e.data.type === 'frame') {
                    const decoder = this.decoderById.get(e.data.streamId);
                    if (decoder) {
                        decoder.deliverFrame(e.data.frame);
                    }
                    else {
                        e.data.frame.close();
                    }
                }
            };
            worker.onmessage = (e) => {
                if (e.data.type === 'log' || e.data.type === 'trackEnded') {
                    console.log(`${this.LOG_PREFIX} [TrackProcessor] ${e.data.msg ?? `track #${e.data.streamId} ended (${e.data.count} frames)`}`);
                }
            };
            this.frameReaderWorker = worker;
            this.frameReaderPort = channel.port1;
            console.log(`${this.LOG_PREFIX} TrackProcessorWorker initialized (worker MSTP path)`);
        }
        catch (err) {
            console.warn(`${this.LOG_PREFIX} TrackProcessorWorker creation failed, will use rVFC fallback:`, err);
        }
    }
    _createTrackProcessorWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./webrtcvideodecoder/TrackProcessorWorker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./webrtcvideodecoder/TrackProcessorWorker${ext}`, import.meta.url);
    }
    _initInjectorWorker() {
        try {
            this.injectorWorker = this._createInjectorWorker();
            this.injectorWorker.onmessage = (e) => {
                if (e.data.type === 'log') {
                    console.log(`${this.LOG_PREFIX} [Injector] ${e.data.msg}`);
                }
            };
            console.log(`${this.LOG_PREFIX} shared RtpInjectorWorker initialized`);
        }
        catch (err) {
            console.warn(`${this.LOG_PREFIX} shared RtpInjectorWorker creation failed, each decoder will create its own:`, err);
        }
    }
    _createInjectorWorker() {
        if (import.meta.env.BUILD_TRANSFORMS_FILENAMES) {
            return new Worker(new URL('./webrtcvideodecoder/RtpInjectorWorker.ts', import.meta.url), { type: 'module' });
        }
        const ext = import.meta.env.DEV ? '.ts' : '.js';
        return createWorker(`./webrtcvideodecoder/RtpInjectorWorker${ext}`, import.meta.url);
    }
    /**
     * Accept a MessagePort from NetworkingWorker for receiving inbound
     * encoded video frames directly (bypassing the main thread).
     */
    connectNetworkingPort(port) {
        console.log(`${this.LOG_PREFIX} connectNetworkingPort called`);
        port.onmessage = (e) => {
            const msg = e.data;
            switch (msg.type) {
                case 'encodedVideoFrame':
                    this.feedEncodedVideoFrame(msg.frame).catch(console.error);
                    break;
                case 'handleLeave':
                    this.handleLeave(msg.userSessionId).catch(console.error);
                    break;
            }
        };
    }
    /**
     * Accept a MessagePort for direct communication with VideoPlayerWorker.
     * Uses Comlink to wrap the port, giving us a proxy to the DecoderPortAPI
     * exposed by VideoPlayerWorker.connectDecoderPort().
     */
    connectPlayerPort(port) {
        console.log(`${this.LOG_PREFIX} connectPlayerPort called`);
        this.playerPortProxy = wrap(port);
    }
    async feedEncodedVideoFrame(frame) {
        if (frame.done) {
            const trackInfo = this.tracks.get(frame.trackId);
            if (trackInfo) {
                console.log(`${this.LOG_PREFIX} done frame, closing decoder for track ${frame.trackId.slice(0, 8)}`);
                trackInfo.decoder.close();
                this.decoderById.delete(trackInfo.decoderId);
                trackInfo.outputTrack.emit(undefined, true);
                this.tracks.delete(frame.trackId);
            }
            return;
        }
        if (!this.tracks.has(frame.trackId)) {
            if (this.creatingTracks.has(frame.trackId)) {
                return;
            }
            await this._createDecoderForTrack(frame);
            // The triggering frame is stale after ICE negotiation — don't feed it.
            // A fresh keyframe will arrive shortly and start the decoder cleanly.
            return;
        }
        const trackInfo = this.tracks.get(frame.trackId);
        if (!trackInfo)
            return;
        if (this.waitingForKeyframe.has(frame.trackId)) {
            if (!frame.isKeyFrame)
                return;
            this.waitingForKeyframe.delete(frame.trackId);
            console.log(`${this.LOG_PREFIX} track ${frame.trackId.slice(0, 8)} synced on fresh keyframe`);
        }
        trackInfo.lastFrameNumber = frame.frameNumber;
        // Push metadata to FIFO so we can correlate decoded output frames
        trackInfo.metadataFifo.push({
            frameNumber: frame.frameNumber,
            timestamp: frame.timestamp,
            duration: frame.duration,
            isScreenSharing: frame.isScreenSharing,
            rotation: frame.rotation,
            isKeyFrame: frame.isKeyFrame,
            arrivalTimeMillis: frame.arrivalTimeMillis,
        });
        // Convert length-prefixed NALUs to Annex-B format.
        // Copy data to a fresh ArrayBuffer to avoid SharedArrayBuffer typing issues.
        const dataCopy = new Uint8Array(frame.data);
        let annexbData;
        if (trackInfo.hasExtradata) {
            annexbData = lengthPrefixedToAnnexB(dataCopy.buffer);
            if (frame.isKeyFrame && trackInfo.paramNalus.length > 0) {
                annexbData = prependNalus(annexbData, trackInfo.paramNalus);
            }
        }
        else {
            annexbData = dataCopy.buffer;
        }
        trackInfo.decoder.feedChunk({
            type: frame.isKeyFrame ? 'key' : 'delta',
            data: annexbData,
        });
    }
    async handleLeave(userSessionId) {
        console.log(`${this.LOG_PREFIX} handleLeave called`, { userSessionId });
        const tracksToRemove = [];
        for (const [trackId, info] of this.tracks.entries()) {
            if (info.userSessionId === userSessionId) {
                tracksToRemove.push(trackId);
            }
        }
        for (const trackId of tracksToRemove) {
            const info = this.tracks.get(trackId);
            if (!info)
                continue;
            info.decoder.close();
            this.decoderById.delete(info.decoderId);
            info.outputTrack.emit(undefined, true);
            this.tracks.delete(trackId);
            console.log(`${this.LOG_PREFIX} stopped decoder for track ${trackId.slice(0, 8)} (session leave)`);
        }
    }
    async setReferenceTimes(trackIds, presentationTimeMicros, realTimeMillis) {
        await this.playerPortProxy?.setReferenceTimes(trackIds, presentationTimeMicros, realTimeMillis);
    }
    async destroy() {
        console.log(`${this.LOG_PREFIX} destroy called`);
        for (const [trackId, info] of this.tracks.entries()) {
            info.decoder.close();
            info.outputTrack.emit(undefined, true);
            console.log(`${this.LOG_PREFIX} destroyed decoder for track ${trackId.slice(0, 8)}`);
        }
        this.tracks.clear();
        this.decoderById.clear();
        if (this.frameReaderPort) {
            this.frameReaderPort.close();
            this.frameReaderPort = null;
        }
        if (this.frameReaderWorker) {
            this.frameReaderWorker.terminate();
            this.frameReaderWorker = null;
        }
        if (this.injectorWorker) {
            this.injectorWorker.terminate();
            this.injectorWorker = null;
        }
        destroyDummyStream();
        console.log(`${this.LOG_PREFIX} destroy completed`);
    }
    // --- Private ---
    async _createDecoderForTrack(frame) {
        this.creatingTracks.add(frame.trackId);
        // Clean up old tracks from the same user session
        for (const [existingTrackId, existingInfo] of this.tracks.entries()) {
            if (existingInfo.userSessionId === frame.userSessionId &&
                existingTrackId !== frame.trackId) {
                console.log(`${this.LOG_PREFIX} replacing old track ${existingTrackId.slice(0, 8)} with ${frame.trackId.slice(0, 8)} for session ${frame.userSessionId}`);
                existingInfo.decoder.close();
                this.decoderById.delete(existingInfo.decoderId);
                existingInfo.outputTrack.emit(undefined, true);
                this.tracks.delete(existingTrackId);
            }
        }
        const isHEVC = frame.codec.startsWith('hvc1') || frame.codec.startsWith('hev1');
        const sdpCodecName = isHEVC ? 'H265' : 'H264';
        const paramNalus = frame.annexb || frame.extradata.byteLength === 0
            ? []
            : isHEVC
                ? extractHEVCParams(frame.extradata)
                : extractAVCParams(frame.extradata);
        const trackId = frame.trackId;
        const outputTrack = new Track(trackId, false);
        const metadataFifo = [];
        const decoderId = this.nextDecoderId++;
        const decoder = new WebRTCVideoDecoder({
            output: (videoFrame) => {
                if (!outputTrack.hasListener() ||
                    outputTrack.getBufferLength() >= DEFAULT_MAX_BUFFERED_FRAMES) {
                    videoFrame.close();
                    return;
                }
                const meta = metadataFifo.shift();
                const unencodedFrame = {
                    frameNumber: meta?.frameNumber ?? 0n,
                    timestamp: meta?.timestamp ?? BigInt(videoFrame.timestamp),
                    duration: meta?.duration ?? BigInt(videoFrame.duration ?? 0),
                    trackId,
                    done: false,
                    isKeyFrame: meta?.isKeyFrame ?? false,
                    codedWidth: videoFrame.codedWidth,
                    codedHeight: videoFrame.codedHeight,
                    isScreenSharing: meta?.isScreenSharing ?? false,
                    rotation: meta?.rotation ?? 0,
                    videoFrame,
                    arrivalTimeMillis: meta?.arrivalTimeMillis,
                };
                outputTrack.emit(unencodedFrame, false, [videoFrame]);
            },
            error: (err) => {
                console.error(`${this.LOG_PREFIX} decoder error for track ${trackId.slice(0, 8)}:`, err.message);
            },
        });
        this.decoderById.set(decoderId, decoder);
        try {
            await decoder.configure({
                sdpCodecName,
                decoderId,
                onLog: (msg) => console.log(`${this.LOG_PREFIX} [dec#${decoderId}] ${msg}`),
                frameReaderWorker: this.frameReaderWorker ?? undefined,
                injectorWorker: this.injectorWorker ?? undefined,
            });
        }
        catch (err) {
            this.decoderById.delete(decoderId);
            this.creatingTracks.delete(trackId);
            throw err;
        }
        this.tracks.set(trackId, {
            decoder,
            decoderId,
            userSessionId: frame.userSessionId,
            lastFrameNumber: 0n,
            paramNalus,
            hasExtradata: frame.extradata.byteLength > 0,
            outputTrack,
            metadataFifo,
        });
        this.creatingTracks.delete(trackId);
        try {
            await this.playerPortProxy?.addVideoTrack(proxy(outputTrack));
            console.log(`${this.LOG_PREFIX} track ${trackId.slice(0, 8)} connected to player`);
        }
        catch (err) {
            console.error(`${this.LOG_PREFIX} addVideoTrack failed:`, err);
        }
        this.waitingForKeyframe.add(trackId);
    }
}
